import { Disclosure, Transition } from "@headlessui/react";
import clsx from "clsx";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ChevronDown } from "@/assets/icons/ds";
import { GlobeLine } from "@/assets/icons/icons";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { useGetLanguages } from "@/hooks/useGetLanguages";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

/** Language switcher pinned to the bottom of the Sidebar/burger drawer; expands as a popover above the trigger. */
export const LanguageMenu = () => {
  const { i18n } = useTranslation();
  const languages = useGetLanguages();
  const rootRef = useRef<HTMLDivElement>(null);
  // Disclosure has no built-in outside-click dismissal (unlike Popover/Menu/Dialog) -- close manually via refs kept fresh below.
  const openRef = useRef(false);
  const closeRef = useRef<() => void>(() => {});

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (
        openRef.current &&
        rootRef.current &&
        !rootRef.current.contains(event.target as Node)
      ) {
        closeRef.current();
      }
    };

    document.addEventListener("mousedown", handleClick);

    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <Disclosure as="div" className="relative">
      {({ open, close }) => {
        openRef.current = open;
        closeRef.current = close;

        return (
          <div ref={rootRef}>
            {/*
              Plain transparent row -- no pill background -- with the chevron
              packed right after the label (4px gap), not pushed to the far edge.
            */}
            {/* !font-semibold: see NavRow — form.scss button reset */}
            <Disclosure.Button
              className="group relative flex w-full items-center gap-1 rounded-md px-2 py-1 text-body-base !font-semibold leading-none text-foreground-primary outline-none hover:bg-surface-tertiary"
              tabIndex={KEYBOARD_TAB_INDEX}
            >
              <FocusRing />
              <GlobeLine className="size-4" />
              {i18n.resolvedLanguage?.toUpperCase()}
              <ChevronDown
                className={clsx(
                  "size-4 shrink-0 text-foreground-primary transition-transform",
                  open && "rotate-180",
                )}
              />
            </Disclosure.Button>
            {/* Positioning wrapper sits outside Transition -- its scale transform would otherwise become the containing block and anchor the panel to the wrong edge. */}
            <div className="absolute left-0 top-0 z-10 w-fit -translate-y-[calc(100%+8px)]">
              <Transition
                as="div"
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Disclosure.Panel
                  as="ul"
                  className="flex w-fit flex-col gap-1 rounded-lg bg-surface-primary p-2"
                >
                  {languages.map(({ label, code }) => {
                    const selected = i18n.resolvedLanguage === code;

                    return (
                      <li key={code}>
                        <button
                          className={clsx(
                            "group relative w-full whitespace-nowrap rounded px-3 py-2.5 text-left text-body-base !font-semibold leading-none text-foreground-primary-inverse outline-none",
                            selected
                              ? "bg-surface-tertiary-inverse"
                              : "hover:bg-surface-quaternary-inverse",
                          )}
                          type="button"
                          tabIndex={KEYBOARD_TAB_INDEX}
                          aria-current={selected ? "true" : undefined}
                          onClick={() => {
                            i18n.changeLanguage(code);
                            close();
                          }}
                        >
                          <FocusRing inverted />
                          {label}
                        </button>
                      </li>
                    );
                  })}
                </Disclosure.Panel>
              </Transition>
            </div>
          </div>
        );
      }}
    </Disclosure>
  );
};
